import { FC, useState, useEffect, memo } from "react";
import { useTranslation } from "react-i18next";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import SessionTimeoutLogo from "assets/SessionTimeoutLogo";
import SessionTimeoutWatch from "assets/SessionTimeoutWatch";
import "./sessionTimeoutModal.css";
import { SESSION_GRACE } from "core/utils/constants";
import {
  StyledModalBody,
  StyledModalFooter,
  StyledModalHeader,
} from "./sessionTimeoutModal.styled";

interface SessionTimeoutModalProps {
  showSessionTimeoutModal: boolean;
  onExtendButtonClick: () => void;
  onLogout: () => void;
}

const SessionTimeoutModal: FC<SessionTimeoutModalProps> = ({
  showSessionTimeoutModal,
  onExtendButtonClick,
  onLogout,
}): JSX.Element => {
  const { t } = useTranslation();
  const [counter, setCounter] = useState<number>(SESSION_GRACE / 1000);
  const minutes = Math.floor(counter / 60);
  const seconds = Math.floor(counter % 60);

  useEffect(() => {
    const timer = setInterval(() => {
      setCounter((prevCounter) => {
        if (prevCounter > 0) {
          return prevCounter - 1;
        } else {
          onLogout();
          clearInterval(timer);
          return prevCounter; // No further updates needed
        }
      });
    }, 1000);
    
    return () => clearInterval(timer); // Cleanup on unmount
  }, [onLogout]);

  return (
    <Modal
      centered
      show={showSessionTimeoutModal}
      aria-labelledby="contained-modal-title-vcenter"
    >
      <StyledModalHeader>
        <span className="session-modal-header">
          <SessionTimeoutLogo />
          <p className="session-modal-title">
            {t("SESSION_TIMEOUT_MODAL.MODALTITLE")}
          </p>
        </span>
        <hr className="modal-header-separator" />
      </StyledModalHeader>
      <StyledModalBody className="modal-bg">
        <p className="session-content-title">
          {t("SESSION_TIMEOUT_MODAL.ABOUTTOEXPIRE")}
        </p>
        <p className="session-content-subtitle">
          {t("SESSION_TIMEOUT_MODAL.LOGOUTIN")}
        </p>
        <p className="session-content-timer session-content-title">
          {minutes}:{seconds.toString().padStart(2, "0")}
        </p>
        <div className="watch-wrapper">
          <SessionTimeoutWatch />
        </div>
      </StyledModalBody>
      <StyledModalFooter>
        <Button
          className="extend-btn action-btn"
          onClick={onExtendButtonClick}
          data-testid="extend-button"
        >
          {t("SESSION_TIMEOUT_MODAL.EXTEND")}
        </Button>
        <Button
          className="logout-btn action-btn"
          variant="outline-secondary"
          onClick={onLogout}
          data-testid="logout-button"
          role={"logout"}
        >
          {t("SESSION_TIMEOUT_MODAL.LOGOUT")}
        </Button>
      </StyledModalFooter>
    </Modal>
  );
};

export default memo(SessionTimeoutModal);
